

import { useState } from 'react';
import { updateDoc, DocumentReference } from 'firebase/firestore';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Trash2, Plus, Edit, StickyNote } from 'lucide-react';
import type { ToDo, Note } from '@/app/lib/types';
import { StickyNoteCard } from './StickyNoteCard';


interface ItineraryEditorProps {
  itineraryRef: DocumentReference;
  initialNotes: Note[];
  initialTodos: ToDo[];
}

export function ItineraryEditor({ itineraryRef, initialNotes, initialTodos }: ItineraryEditorProps) {
  const [notes, setNotes] = useState<Note[]>(initialNotes);
  const [todos, setTodos] = useState<ToDo[]>(initialTodos);
  const [newTodoText, setNewTodoText] = useState('');

  const handleUpdateNote = async (noteId: string, content: string) => {
    const updatedNotes = notes.map(note => 
      note.id === noteId ? { ...note, content } : note
    );
    setNotes(updatedNotes);
    await updateDoc(itineraryRef, { notes: updatedNotes });
  };

  const handleAddNote = async () => {
    const newNote: Note = {
      id: crypto.randomUUID(),
      content: 'New scribble...',
    };
    const updatedNotes = [...notes, newNote];
    setNotes(updatedNotes);
    await updateDoc(itineraryRef, { notes: updatedNotes });
  };

  const handleDeleteNote = async (noteId: string) => {
    const updatedNotes = notes.filter(note => note.id !== noteId);
    setNotes(updatedNotes);
    await updateDoc(itineraryRef, { notes: updatedNotes });
  };

  const handleAddTodo = async () => {
    if (newTodoText.trim() === '') return;
    const newTodo: ToDo = {
      id: crypto.randomUUID(),
      text: newTodoText,
      completed: false,
    };
    const updatedTodos = [...todos, newTodo];
    setTodos(updatedTodos);
    await updateDoc(itineraryRef, { todos: updatedTodos });
    setNewTodoText('');
  };

  const handleToggleTodo = async (todoId: string) => {
    const updatedTodos = todos.map((todo) =>
      todo.id === todoId ? { ...todo, completed: !todo.completed } : todo
    );
    setTodos(updatedTodos);
    await updateDoc(itineraryRef, { todos: updatedTodos });
  };

  const handleDeleteTodo = async (todoId: string) => {
    const updatedTodos = todos.filter((todo) => todo.id !== todoId);
    setTodos(updatedTodos);
    await updateDoc(itineraryRef, { todos: updatedTodos });
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-8 my-8">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <StickyNote className="h-6 w-6 text-primary" />
              Chieftain's Scribbles
            </div>
            <Button size="sm" onClick={handleAddNote}>
              <Plus className="h-4 w-4 mr-2" /> Add Scribble
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
           <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-h-[400px] overflow-y-auto p-1">
             {notes.length > 0 ? (
                notes.map(note => (
                  <StickyNoteCard 
                    key={note.id}
                    note={note}
                    onUpdate={handleUpdateNote}
                    onDelete={handleDeleteNote}
                  />
                ))
             ) : (
                <div className="col-span-full text-center text-muted-foreground py-8">
                    No scribbles yet. Add one to get started!
                </div>
             )}
           </div>
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Edit className="h-6 w-6 text-primary" />
            Provisions Checklist
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex gap-2 mb-4">
            <Input
              placeholder="e.g., Pack midge repellent"
              value={newTodoText}
              onChange={(e) => setNewTodoText(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleAddTodo()}
            />
            <Button onClick={handleAddTodo}><Plus className="h-4 w-4 mr-2"/> Add</Button>
          </div>
          <div className="space-y-3 max-h-64 overflow-y-auto pr-2">
            {todos.length > 0 ? (
              todos.map((todo) => (
                <div key={todo.id} className="flex items-center gap-3 group">
                  <Checkbox
                    id={`todo-${todo.id}`}
                    checked={todo.completed}
                    onCheckedChange={() => handleToggleTodo(todo.id)}
                    className="h-5 w-5"
                  />
                  <label
                    htmlFor={`todo-${todo.id}`}
                    className={`flex-grow text-sm ${todo.completed ? 'line-through text-muted-foreground' : ''}`}
                  >
                    {todo.text}
                  </label>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 opacity-0 group-hover:opacity-100 transition-opacity"
                    onClick={() => handleDeleteTodo(todo.id)}
                  >
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                </div>
              ))
            ) : (
              <p className="text-sm text-muted-foreground text-center py-4">No provisions listed yet. Add one above!</p>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
